import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle } from 'lucide-react';

export const ContactModal = ({ isOpen, onClose, prefillMessage = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  useEffect(() => {
    if (isOpen && prefillMessage) {
      setFormData((prev) => ({ ...prev, message: prefillMessage }));
    }
  }, [isOpen, prefillMessage]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) throw new Error('Failed to send');
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', company: '', message: '' });
        onClose();
      }, 2000);
    } catch {
      setIsSubmitting(false);
      alert('Something went wrong. Please try again.');
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
      // Reset state when closing
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', company: '', message: '' });
      }, 200);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        data-testid="contact-form-modal"
        className="sm:max-w-md bg-white border"
        style={{ borderColor: '#E2E8F0' }}
      >
        <DialogHeader>
          <DialogTitle 
            className="text-xl font-semibold"
            style={{ color: '#131B2E' }}
          >
            Book a Strategy Call
          </DialogTitle>
          <DialogDescription 
            className="text-sm"
            style={{ color: '#475569' }}
          >
            Fill out the form below and our team will get back to you within 24 hours.
          </DialogDescription>
        </DialogHeader>

        {isSubmitted ? (
          <div 
            className="flex flex-col items-center justify-center py-8"
            data-testid="contact-form-success"
          >
            <CheckCircle 
              className="h-12 w-12 mb-4" 
              style={{ color: '#0A3D91' }}
            />
            <p 
              className="text-lg font-medium"
              style={{ color: '#131B2E' }}
            >
              Thank you!
            </p>
            <p 
              className="text-sm"
              style={{ color: '#475569' }}
            >
              We'll be in touch soon.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label 
                htmlFor="name"
                className="text-sm font-medium"
                style={{ color: '#131B2E' }}
              >
                Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleInputChange}
                placeholder="John Doe"
                data-testid="contact-input-name"
                className="border focus:ring-2 focus:ring-offset-0"
                style={{ 
                  borderColor: '#E2E8F0',
                  '--tw-ring-color': '#00BFFF'
                }}
              />
            </div>

            <div className="space-y-2">
              <Label 
                htmlFor="email"
                className="text-sm font-medium"
                style={{ color: '#131B2E' }}
              >
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john@company.com"
                data-testid="contact-input-email"
                className="border focus:ring-2 focus:ring-offset-0"
                style={{ 
                  borderColor: '#E2E8F0',
                  '--tw-ring-color': '#00BFFF'
                }}
              />
            </div>

            <div className="space-y-2">
              <Label 
                htmlFor="company"
                className="text-sm font-medium"
                style={{ color: '#131B2E' }}
              >
                Company
              </Label>
              <Input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Company Inc."
                data-testid="contact-input-company"
                className="border focus:ring-2 focus:ring-offset-0"
                style={{ 
                  borderColor: '#E2E8F0',
                  '--tw-ring-color': '#00BFFF'
                }}
              />
            </div>

            <div className="space-y-2">
              <Label 
                htmlFor="message"
                className="text-sm font-medium"
                style={{ color: '#131B2E' }}
              >
                Message
              </Label>
              <Textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your project..."
                rows={4}
                data-testid="contact-input-message"
                className="border focus:ring-2 focus:ring-offset-0 resize-none"
                style={{ 
                  borderColor: '#E2E8F0',
                  '--tw-ring-color': '#00BFFF'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
              className="w-full kv-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
